import { Component, type ErrorInfo, type ReactNode } from 'react';
import { GenericErrorPage } from '@locus-taxy/encore-ui-toolkit';
import { telemetryService } from '@locus-taxy/encore-observability/telemetry';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  showRetry?: boolean;
  errorTitle?: string;
  errorMessage?: string;
  retryButtonText?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    telemetryService.recordException(error.message, error, errorInfo);
  }

  private handleRetry = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const {
        showRetry = true,
        errorTitle,
        errorMessage,
        retryButtonText,
      } = this.props;

      return (
        <GenericErrorPage
          title={errorTitle}
          message={errorMessage}
          showRetry={showRetry}
          onRetry={this.handleRetry}
          retryButtonText={retryButtonText}
        />
      );
    }

    return this.props.children;
  }
}
